import { 
    validateResourceRule,
    timeBlocksCollide,
    serviceFactory,
    toTimestamp,
    getDuration,
    CALENDAR_TYPE,
    TIMETABLE_TYPE,
    TIMEBLOCK_TYPE
} from '../common/ReservationService.js'

const moment = window.moment;

if (typeof window.wisenetwork !== 'undefined' 
        && typeof window.wisenetwork.langCode !== 'undefined') {
    moment.locale(window.wisenetwork.langCode);
}

let today = moment();

const queryString = location.search;
const params = new URLSearchParams(queryString);
const memoryDate = localStorage['reservationSelectedDate']

if (params.get('date')) {
    const queryDate = moment(`${ params.get('date') } 23:59:59`)
    
    if (queryDate != 'Invalid Date' && queryDate >= today) {
        today = queryDate
        localStorage['reservationSelectedDate'] = JSON.stringify(queryDate.format('YYYY-MM-DD'))
    }
} else if (memoryDate) {
    try {
        const parsedDate = moment(`${ JSON.parse(memoryDate) } 23:59:59`)

        if (parsedDate != 'Invalid Date' && parsedDate >= today) {
            today = parsedDate;
        } else {
            delete localStorage['reservationSelectedDate']
        }
    } catch (e) {
        today = moment()

        delete localStorage['reservationSelectedDate']
    }
}

const resetTime = { hour: 0, minute: 0, second: 0, millisecond: 0 };
const settingsFactory = () => ({
    'reservationId':null,
    'productId':null,
    'type':3,
    'recurrence':null,
    'recurrenceDays':[true,true,true,true,true,true,true],
    'startDate':null,
    'endDate':null,
    'startTime':null,
    'endTime':null,
    'duration':null,
    'break':null,
    'leadTime':null,
    'quantity':null,
    'minimumOrderQuantity':null,
    'minDuration':null,
    'maxDuration':null,
    'hideQuantity':null,
    'hideEndTime':null,
    'limitFutureReservations':null,
    'disallowReservationsWithin': null,
    'minDurationBetweenReservations':null,
    'groupResourcesAsProducts': null,
    'resources':[],
    'additionalResources':[],
    'showNamesPublic': false,
})
let service = null;

export default {
    namespaced: true,
    state: {
        calendarType: null,
        disabled: false,
        dateChangeInProgress: false,
        isInvalidDate: false,
        weekMode: false,
        settings: settingsFactory(),
        reservations: [],
        recurrenceDays: [],
        startTime: null,
        endTime: null,
        startDate: null,
        endDate: null,
        selectedDate: today,
        activeWeek: null,
        resourceRules: [],
        resourceComments: [],
        additionalResources: [],
        reservationsAdditionalResources: [],
        userReservations: [],
        availabilityRate: [],
    },
    getters: {
        CALENDAR_TYPE: () => CALENDAR_TYPE,
        TIMEBLOCK_TYPE: () => TIMEBLOCK_TYPE,
        reservations: (state) => state.reservations,
        reservationsAdditionalResources: (state) => {
            return (filter) => state.reservationsAdditionalResources.filter(filter)
        },
        calendarType: (state) => state.calendarType,
        date: (state) => state.selectedDate,
        dateRestrictions: (state) => {
            return {
                start: state.startDate ? state.startDate.set(resetTime) : null,
                end: state.endDate ? state.endDate.set({ hour: 23, minute: 59, second: 59, millisecond: 0 }) : null
            }
        },
        isInvalidDate: (state, getters, rootState, rootGetters) => (date) => {
            const weekCopy = [...state.recurrenceDays]
            const sunday = weekCopy.pop()
            let disabled = false
            
            if (!rootGetters['common/adminMode']) {
                const days = Math.round(
                    moment.duration(
                        date.set(resetTime) - moment().set(resetTime)
                    ).asDays()
                )
                const [ calendarVisibility ] = state.resourceRules.filter(
                    row => row.ruleName === 'kalenteriNakyvyysPaivat' && row.isUserRule
                )
                const limitFutureReservations = state.settings.limitFutureReservations;

                if (typeof calendarVisibility !== 'undefined') {
                    if (days > calendarVisibility.ruleValue) {
                        disabled = true;
                    }
                } else if (typeof limitFutureReservations === 'number' && limitFutureReservations !== 0) {
                    if (days > limitFutureReservations) {
                        disabled = true;
                    }
                }

                if (days < 0) {
                    disabled = true;
                }
            }
            
            if ((state.startDate && date.isBefore(state.startDate, 'day')) 
                || (state.endDate && date.isAfter(state.endDate, 'day'))) {
                disabled = true;
            }

            weekCopy.unshift(sunday);
            weekCopy.forEach((day, i) => {
                if (!day && date.day() === i) {
                    disabled = true;
                }
            })

            return disabled;
        },
        weekMode: (state, getters, rootState, rootGetters) => {
            const timeTable = rootGetters['calendar/timeTable'];
            
            // this is used here for prevent problems during
            // the calendar weekmode change process
            if (timeTable.length > 0 && !!timeTable[0].days) {
                return true;
            }
            
            return state.weekMode;
        },
        resourceRules: (state) => state.resourceRules.filter(row => validateResourceRule(row, state.selectedDate)),
        resourceComments: (state) => state.resourceComments,
        resourceCommentsByBlock: (state, getters, rootState, rootGetters) => {
            const adminMode = rootGetters['common/adminMode'];

            return (block) => state.resourceComments.filter(row => {
                if (typeof block === 'undefined') return false;
                if (!row.public && !adminMode) return false;
                const { resource } = block;
                
                if (resource.resourceId !== row.resourceId) return false;
                // if comment validity collides with the block, return it
                const collision = timeBlocksCollide(
                    toTimestamp(row.dateTimeStart),
                    toTimestamp(row.dateTimeEnd),
                    block.start.format('X'),
                    block.end.format('X')
                )
                
                return collision;
            })
        },
        generateReservation: (state) => (index, table, config = {}) => service.generateReservation(index, table, config),
        service: (state) => service,
        settings: (state) => state.settings,
        startTime: (state) => state.startTime,
        endTime: (state) => state.endTime,
        startDate: (state) => state.startDate,
        endDate: (state) => state.endDate,
        disabled: (state) => state.disabled,
        dateChangeInProgress: (state) => state.dateChangeInProgress,
        additionalResources: (state) => state.additionalResources,
        userReservations: (state) => state.userReservations,
        availabilityRate: (state) => state.availabilityRate
            .filter(row => !row.disabled)
            .sort((a, b) => {
                if (a.available && !b.available) return -1;
                if (!a.available && b.available) return 1;
                return 0;
            })
            .map(row => row.resourceId),
        disabledResources: (state) => state.availabilityRate
            .filter(row => row.disabled)
            .map(row => row.resourceId),
        minuteRules(state, getters) {
            const resourceRules = getters['resourceRules'];
            return resourceRules
                .filter(row => row.ruleName === 'minuuttiHinta' && (!row.categoryId || row.isUserRule))
        },
        minutePrice(state, getters, rootState, rootGetters) {
            const minuteRules = getters['minuteRules']

            if (!minuteRules.length) return null;

            const activeReservation = rootGetters['calendar/activeReservation'];
            const adminMode = rootGetters['common/adminMode'];
            const { start, end } = activeReservation;
            const duration = getDuration(start, end)
            let price = null
            
            for (let row of minuteRules) {
                if (typeof row.ruleValue[duration] !== 'undefined') {
                    price = row.ruleValue[duration]
                    break;
                }
            }

            return price
        },
        showNamesPublic(state) {
            return state.settings?.showNamesPublic || false
        },
    },
    mutations: {
        setServiceAdminMode(state, value) {
            service.settings.admin = value;
        },
        setDisabled(state, value) {
            state.disabled = value;
        },
        setWeekMode(state, value) {
            state.weekMode = value;
        },
        toggleWeekMode(state, value) {
            if (typeof value !== 'undefined') {
                state.weekMode = value;
            } else {
                state.weekMode = !state.weekMode;
            }
            // localstorage should remember the state by productId
            //localStorage['reservationAdminMode'] = JSON.stringify(state.adminMode);
        },
        setResourceRules(state, rows) {
            const ids = state.resourceRules.map(row => row.ruleId);
            const newRows = rows.filter(row => !ids.includes(row.ruleId));
            
            state.resourceRules = [
                ...state.resourceRules,
                ...newRows,
            ];
        },
        removeResourceRule(state, ruleId) {
            const index = state.resourceRules.findIndex(row => row.ruleId === ruleId)
            state.resourceRules.splice(index, 1)
        },
        updateResourceRule(state, data) {
            const { ruleId, usableQuantity } = data;
            const rule = state.resourceRules.filter(row => row.ruleId == ruleId)[0]

            if (!rule) return;

            rule.usableQuantity = usableQuantity;
        },
        setResourceComments(state, rows) {
            const ids = state.resourceComments.map(row => row.commentId);
            const newRows = rows.filter(row => ids.indexOf(row.commentId) === -1);
            
            state.resourceComments = [
                ...state.resourceComments,
                ...newRows,
            ];
        },
        setAdditionalResources(state, rows) {
            state.additionalResources = rows.filter(row => {
                // to ease product creator's life
                if (!row.productId) {
                    console.error(`Additional resource, resourceId: ${ row.resourceId } is missing a productId!`)
                }
                
                return !!row.productId;
            })
        },
        setUserReservations(state, rows) {
            state.userReservations = rows;
        },
        setReservationsAdditionalResources(state, rows) {
            state.reservationsAdditionalResources = rows
            .map(row => {
                row.resourceCategory = row.resourceCategory || '';

                if (row.resourceCategory.includes('-')) {
                    // eslint-disable-next-line no-unused-vars
                    const [ type, number ] = row.resourceCategory.split('-')
                    
                    row.resourceNumber = number;
                }

                row.inFuture = moment(row.dateTimeStart) > moment()

                return row;
            })
        },
        setAvailabilityRate(state, rows) {
            state.availabilityRate = rows;
        },
    },
    actions: {
        init({ state, commit, dispatch, rootGetters }) {
            const product = rootGetters['ecom/product'];
            const admin = rootGetters['common/adminMode'];
            const settings = state.settings;
            
            service = serviceFactory({
                product,
                settings,
                selectedDate: state.selectedDate,
                reservations: state.reservations,
                admin,
                resourceRules: state.resourceRules,
            });
            
            state.calendarType = settings.type;
            
            if ([
                CALENDAR_TYPE.weekCalendar,
                CALENDAR_TYPE.resource,
                CALENDAR_TYPE.resourceEquipment
            ].includes(settings.type)) {
                document.querySelector('.calendar-table-info').classList.remove('hidden')
            }
            
            if (settings.type === CALENDAR_TYPE.weekCalendar) {
                commit('setWeekMode', true)
            } else if (settings.type === CALENDAR_TYPE.grid) {
                commit('calendar/setViewMode', 'grid', { root: true });
            } else if (settings.type === CALENDAR_TYPE.resource) {
            }
            
            let baseTableEndTime = moment(
                `${ state.selectedDate.format('YYYY-MM-DD') } ${ state.endTime }`
            )
            // add some buffer
            if (state.startTime !== state.endTime) {    
                baseTableEndTime = baseTableEndTime.add(1, 'hours')
            }
            
            const baseTimeTable = service.createDayTimeBlocks(
                state.selectedDate, 
                state.startTime, 
                baseTableEndTime.format('HH:mm:ss')
            );

            const timeTable = service.data;
            
            return dispatch('calendar/initTimeTables', { baseTimeTable, timeTable }, { root: true });
        },
        updateService({ state, dispatch, rootGetters }) {
            service.settings.resources = state.settings.resources;
            service.settings.reservations = state.reservations;
            service.init();
            let baseTimeTable = [];
            
            if (state.settings.type !== CALENDAR_TYPE.golf) {
                if (rootGetters['calendar/baseTimeTable'].length === 0) {
                    baseTimeTable = service.createDayTimeBlocks(state.selectedDate, state.startTime, state.endTime);
                } else {
                    baseTimeTable = rootGetters['calendar/baseTimeTable']
                }
            }
            
            const timeTable = service.data;
            
            return dispatch('calendar/initTimeTables', { baseTimeTable, timeTable }, { root: true })
        },
        getReservationSettings({ state, getters, commit, dispatch, rootGetters }, args) {
            const { date, firstLoad = false } = args;
            const product = rootGetters['ecom/product'];
            const EcomService = rootGetters['ecom/EcomService'];
            const admin = rootGetters['common/adminMode'];
            
            return EcomService.getReservationSettings(
                product.productId, 
                date.format('YYYY-MM-DD')
            ).then((response) => {
                const { data } = response;
                const settings = data.reservationSettings;
                                    
                state.settings = settings;
                state.recurrenceDays = settings.recurrenceDays;
                state.startTime = settings.startTime;
                state.endTime = settings.endTime;

                commit('setAdditionalResources', settings.additionalResources);
                
                if (typeof data.golfClubId !== 'undefined') {
                    commit('golf/setDefaultClubId', data.golfClubId, { root: true });
                }

                if (typeof data.resourceRules !== 'undefined') {
                    const resourceRules = data.resourceRules;
                    const handicapDefaults = resourceRules.filter(row => 
                        row.ruleName === 'tasoitusraja' 
                        && typeof row.ruleValue === 'object' 
                        && typeof row.ruleValue.default === 'number'
                    );
                    
                    commit('setResourceRules', resourceRules);
                    
                    if (handicapDefaults.length > 0) {
                        commit('golf/setDefaultHandicaps', handicapDefaults, { root: true })
                    }
                }
                
                if (firstLoad) {
                    if (!!settings.startDate) { 
                        state.startDate = moment(`${ settings.startDate } ${ settings.startTime }`);
                    } else {
                        state.startDate = moment();
                    }
                    
                    if (!!settings.endDate) {
                        state.endDate = moment(`${ settings.endDate } ${ settings.endTime }`);
                    }
                    // today is defined on top of this file as a base date for all things
                    state.selectedDate = state.startDate.format('x') >= today.format('x') ? state.startDate : today;
                    state.activeWeek = state.selectedDate.week();
                    state.isInvalidDate = getters.isInvalidDate(state.selectedDate)
                    
                    /*
                    if(!!settings.disallowReservationsWithin && !admin) {
                        state.selectedDate = moment().add(settings.disallowReservationsWithin, 'hours');
                        state.startDate = state.selectedDate;
                    }
                    */
                    
                    if (state.startDate > moment()) {
                        dispatch('getReservationSettings', { date: state.startDate });
                        
                        return {
                            date: state.startDate
                        }
                    }
                }

                return {
                    date
                }
            })
        },
        getReservations({ state, commit, dispatch, rootGetters }, args) {
            const { date, force } = args;
            const product = rootGetters['ecom/product'];
            const EcomService = rootGetters['ecom/EcomService'];
            
            return EcomService.getReservations(
                product.productId, 
                date.format('YYYY-MM-DD'),
                state.weekMode,
                state.settings.type
            ).then((response) => {
                const { data } = response;
                
                if (Array.isArray(data.rows) === false) {
                    return;
                }

                const oldStatusString = state.statusString;
                const statusString = data.rows.map(row => `${ row.reservationTimeId }-${ row.status }`).join('|');
                //console.log('reservationCount', {force}, state.reservations.length, data.rows.length)
                data.rows.forEach(({ reservationTimeId, status }) => commit('setReservationStatus', { reservationTimeId, status }));
                
                state.statusString = statusString;
                state.paymentDelayed = data.rows.filter(row => row.orderStatusId != 2).map(row => row.reservationTimeId)
                state.reservationInCart = data.rows.filter(row => row.isCart).map(row => row.reservationTimeId)
                state.refreshed = Date.now();

                if (Array.isArray(data.resourceComments)) {
                    commit('setResourceComments', data.resourceComments);
                }

                if (Array.isArray(data.reservationsAdditionalResources)) {
                    commit('setReservationsAdditionalResources', data.reservationsAdditionalResources)
                }
                
                if (Array.isArray(data.availabilityRate)) {
                    commit(
                        'setAvailabilityRate',
                        data.availabilityRate
                    )
                }
                
                // CALENDAR OPTIMIZATION - check if anything really changes in main reservations
                if (!force && state.reservations.length === data.rows.length && statusString === oldStatusString) {
                    return Promise.resolve();
                }
                
                state.reservations = data.rows;

                if (Array.isArray(data.reservationsGolfPlayers)) {
                    const reservationsGolfPlayers = data.reservationsGolfPlayers
                    .map(row => {
                        if (typeof row.accessControlCategories === 'string') {
                            row.accessControlClass = row.accessControlCategories.split('|')
                                .map(row => `person-membership-${ row.toLowerCase().replaceAll(" ","-") }`)
                        }
                        
                        return row;
                    })

                    commit(
                        'golf/setReservationPlayers', 
                        reservationsGolfPlayers, 
                        { root: true }
                    );
                }

                return dispatch('updateService');
            })
        },
        onWeekModeChange({ state, dispatch, rootGetters }) {
            state.dateChangeInProgress = true;
            
            const EcomService = rootGetters['ecom/EcomService'];
            const product = rootGetters['ecom/product'];
            const admin = rootGetters['common/adminMode'];
            const settings = state.settings;
            
            return EcomService.getReservations(
                product.productId, 
                state.selectedDate.format('YYYY-MM-DD'),
                state.weekMode
            ).then((response) => {
                const { data } = response;
                state.reservations = data.rows;
                state.dateChangeInProgress = false;

                if (state.weekMode) {
                    settings.type = CALENDAR_TYPE.weekCalendar;
                } else {
                    settings.type = CALENDAR_TYPE.resource;
                }

                service = serviceFactory({
                    product,
                    settings,
                    selectedDate: state.selectedDate,
                    reservations: state.reservations,
                    admin,
                    resourceRules: state.resourceRules,
                });
                //console.log('onWeekModeChange', service.data, settings.type)
                return dispatch('updateService')
            })
        },
        onDateChange({ state, getters, dispatch, rootGetters }, newDay) {
            const admin = rootGetters['common/adminMode'];

            state.dateChangeInProgress = true;
            // for weekCalendar type
            if (state.activeWeek !== newDay.week()) {
                // get reservations and availabilities for new week
                // generate new weekCalendar
                state.activeWeek = newDay.week();
            } else {
                // highlight the day?
            }
            
            service.onDateChange(newDay, state.startTime, state.endTime);
            
            if (newDay.format('YYYY-MM-DD') >= moment().format('YYYY-MM-DD')) {
                localStorage['reservationSelectedDate'] = JSON.stringify(newDay.format('YYYY-MM-DD'))
            }

            return dispatch('getReservations', { date: newDay, force: true })
                .then(() => {
                    state.selectedDate = newDay;
                    state.isInvalidDate = getters.isInvalidDate(newDay);
                    state.dateChangeInProgress = false;
                })
        },
        deleteResourceComment({ state, commit, rootGetters }, commentData) {
            const EcomService = rootGetters['ecom/EcomService'];
            const { productId } = rootGetters['ecom/product'];

            return EcomService.pushResourceComment({ 
                productId,
                commentId: commentData.commentId, 
                delete: true 
            })
            .then(response => {
                const { data } = response;

                if (data.success) {
                    const index = state.resourceComments.findIndex(row => row.commentId === commentData.commentId);
                    state.resourceComments.splice(index, 1);
                }

                return response;
            })
        },
        addResourceComment({ state, commit, rootGetters }, commentData) {
            const EcomService = rootGetters['ecom/EcomService'];
            const { productId } = rootGetters['ecom/product'];

            commentData.productId = productId;

            return EcomService.pushResourceComment(commentData)
                .then(response => {
                    const { data } = response;

                    if (data.success) {
                        if (!commentData.commentId) {
                            const { 
                                commentId, 
                                createdByUserId, 
                                firstName, 
                                familyName 
                            } = data;

                            commit('setResourceComments', [
                                { 
                                    ...commentData, 
                                    commentId, 
                                    createdByUserId, 
                                    firstName, 
                                    familyName,
                                }
                            ])
                        } else {
                            // update comment
                            const index = state.resourceComments.findIndex(row => row.commentId === commentData.commentId);
                            const comment = state.resourceComments[index];

                            if (typeof comment !== 'undefined') {
                                if (data.deleted) {
                                    state.resourceComments.splice(index, 1);
                                } else {
                                    Object.keys(commentData).forEach(key => {
                                        comment[key] = commentData[key];
                                    })
                                }
                            }
                        }
                    }

                    return response;
                })
        },
        getUserReservations({ commit, rootGetters }, params = {}) {
            const EcomService = rootGetters['ecom/EcomService'];
            const product = rootGetters['ecom/product'];
            
            params.productid = product.productId;

            return EcomService.getUserReservations(params)
                .then((response) => {
                    const { data } = response;

                    if (Array.isArray(data.rows)) {
                        commit('setUserReservations', data.rows);
                    }
                })
        },
    }
}